/**
 * The clinic software's billing feed, as it arrives and as it adds up.
 *
 * One strip of numbers, then one list at a time: each clinic-day's billed
 * revenue, GST and money received, with the cash the drawer reports beside it;
 * every receipt against its invoice and against where it landed; card and UPI
 * against the bank; GST by rate; and the pushes themselves. A day opens beside
 * the list with every row the clinic recorded for it. Where the clinic sends its
 * changes, and the backstop pull, sit behind "Feed set-up" -- needed once, not
 * daily.
 *
 * Nothing on this screen is in the ledger. Revenue is still recognised from
 * the takings sheet; this is the clinic's own record, held so the two can be
 * compared and, once that comparison is trusted, so this can replace it.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { ErrorNote, Loading, Note, Pill, Stat } from '../components/ui'
import { PAGE, Pager, usePaged } from '../components/Pager'

interface Delivery {
  delivery_id: string
  received_at: string
  event_count: number
  applied: number
  stale: number
  refused: number
  outcome: string
  problem: string | null
}

interface Pull {
  id: string
  started_at: string
  window_from: string
  window_to: string
  trigger: string
  rows_seen: number
  applied: number
  outcome: string
  error: string | null
}

interface BillingSummary {
  webhook_url: string
  secret_configured: boolean
  reconcile_from: string
  last_delivery: Delivery | null
  recent_deliveries: Delivery[]
  last_pull: Pull | null
  records_by_kind: Record<string, number>
  refused_events: number
  unknown_clinics: string[]
}

interface BillingDay {
  clinic_code: string
  clinic_name: string | null
  business_date: string
  invoices: number
  voided: number
  billed_paise: number
  gst_paise: number
  received_paise: number
  received_cash_paise: number
  received_upi_paise: number
  received_card_paise: number
  received_other_paise: number
  refunded_paise: number
  net_received_paise: number
  written_off_paise: number
  cash_net_paise: number
  drawer_cash_net_paise: number | null
  cash_difference_paise: number | null
}

interface BillingRecord {
  id: string
  feed_id: string
  kind: string
  business_date: string | null
  clinic_code: string
  patient_label: string | null
  number: string | null
  status: string | null
  method: string | null
  counts_as: string | null
  amount_paise: number
  gst_paise: number
  cancelled: boolean
  deleted: boolean
  invoice_feed_id: string | null
  source: string
  occurred_at: string
  problem: string | null
}

interface SettlementDay {
  clinic_code: string
  clinic_name: string | null
  business_date: string
  card_debit_paise: number
  card_credit_paise: number
  card_gross_paise: number
  card_settled_paise: number | null
  mdr_paise: number | null
  card_verdict: string
  card_note: string
  upi_taken_paise: number
  upi_on_statement_paise: number
  upi_verdict: string
  upi_note: string
}

interface UnplacedLine {
  txn_date: string
  amount_paise: number
  narration: string
  kind: string
  for_date: string | null
  terminal: string | null
  note: string | null
}

interface Terminal {
  terminal: string
  clinic_code: string
}

interface Receipt {
  feed_id: string
  business_date: string
  clinic_code: string
  patient_label: string | null
  method: string
  counts_as: string
  amount_paise: number
  receipt_no: string | null
  invoice_number: string | null
  invoice_total_paise: number | null
  invoice_status: string | null
  invoice_verdict: string
  invoice_note: string
  source_verdict: string
  source_note: string
  needs_attention: boolean
}

interface MoneyIn {
  start: string
  end: string
  statement_through: string | null
  receipts_count: number
  receipts_paise: number
  attention_count: number
  totals: { verdict: string; count: number; amount_paise: number }[]
  receipts: Receipt[]
}

const SOURCE_CLASS: Record<string, string> = {
  'in the drawer': 'approved',
  'in the bank': 'approved',
  'bank differs': 'returned',
  'not in the drawer': 'returned',
  'not found': 'returned',
}

const INVOICE_CLASS: Record<string, string> = {
  matched: 'approved',
  advance: 'advisory',
  'invoice voided': 'returned',
  'invoice not received': 'returned',
}

interface Settlements {
  rates: string
  statement_through: string | null
  days: SettlementDay[]
  unplaced: UnplacedLine[]
  terminals: Terminal[]
}

const VERDICT_CLASS: Record<string, string> = {
  agrees: 'approved',
  differs: 'returned',
  'credit card rate not set': 'advisory',
  'no settlement found': 'returned',
}

//: A card or UPI verdict that wants a person: anything but "agrees" or blank.
const settlementOff = (d: SettlementDay) =>
  [d.card_verdict, d.upi_verdict].some((v) => v && v !== 'agrees')

function Verdict({ value }: { value: string }) {
  if (!value) return <span className="faint">—</span>
  const cls = VERDICT_CLASS[value]
  return cls ? (
    <span className={`pill ${cls}`}>{value}</span>
  ) : (
    <span className="faint">{value}</span>
  )
}

interface GstRate {
  rate: string
  taxable_paise: number
  tax_paise: number
}

const KIND_LABEL: Record<string, string> = {
  invoice: 'invoice',
  payment: 'payment',
  refund: 'refund',
  ledger: 'patient account',
  write_off: 'write-off',
  protocol_sale: 'treatment package',
}

const when = (iso: string) => iso.slice(0, 16).replace('T', ' ')

//: A panel beside the list: a day's rows, or the feed's set-up.
function Side({
  kicker,
  title,
  sub,
  amount,
  badge,
  onClose,
  children,
}: {
  kicker?: string
  title: string
  sub?: ReactNode
  amount?: string
  badge?: ReactNode
  onClose: () => void
  children: ReactNode
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={title}>
        <header className="drawer-head">
          <div>
            {kicker && <div className="appr-kind">{kicker}</div>}
            <h2>{title}</h2>
            {sub && <div className="muted">{sub}</div>}
          </div>
          {(amount || badge) && (
            <div className="drawer-amount">
              {amount && <div className="value">{amount}</div>}
              {badge}
            </div>
          )}
          <button className="quiet close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        <div className="drawer-body">{children}</div>
      </aside>
    </>
  )
}

function CashCheck({ d, full }: { d: BillingDay; full?: boolean }) {
  if (d.cash_difference_paise === null) return <span className="faint">no drawer rows</span>
  if (d.cash_difference_paise === 0) return <span className="pill approved">agrees</span>
  return (
    <>
      <span className="pill returned">
        differs{full ? ` by ${formatInr(Math.abs(d.cash_difference_paise))}` : ''}
      </span>
      <div className="faint">
        billing {formatInr(d.cash_net_paise)} · drawer {formatInr(d.drawer_cash_net_paise ?? 0)}
      </div>
    </>
  )
}

//: Every row the clinic recorded for one day, with the day's sums on top.
function DayPanel({ day, onClose }: { day: BillingDay; onClose: () => void }) {
  const rows = useQuery({
    queryKey: ['clinic-billing', 'records', day.clinic_code, day.business_date],
    queryFn: () =>
      api.get<BillingRecord[]>(
        `/clinic-billing/records?clinic=${day.clinic_code}&on=${day.business_date}`,
      ),
  })
  const [kind, setKind] = useState('')
  const kinds = Array.from(new Set((rows.data ?? []).map((r) => r.kind))).sort()
  const shown = (rows.data ?? []).filter((r) => !kind || r.kind === kind)
  const paged = usePaged(shown, kind, 25)

  return (
    <Side
      kicker="Clinic-day"
      title={`${day.clinic_name ?? day.clinic_code} — ${day.business_date}`}
      sub={`${day.invoices} bill${day.invoices === 1 ? '' : 's'}${day.voided ? `, ${day.voided} voided` : ''} · GST ${formatInr(day.gst_paise)}`}
      amount={formatInr(day.billed_paise)}
      badge={<CashCheck d={day} full />}
      onClose={onClose}
    >
      <section>
        <h3>Received</h3>
        <dl className="appr-facts">
          <div>
            <dt>Cash</dt>
            <dd>{formatInr(day.received_cash_paise)}</dd>
          </div>
          <div>
            <dt>UPI</dt>
            <dd>{formatInr(day.received_upi_paise)}</dd>
          </div>
          <div>
            <dt>Card</dt>
            <dd>{formatInr(day.received_card_paise)}</dd>
          </div>
          <div>
            <dt>Other</dt>
            <dd>{formatInr(day.received_other_paise)}</dd>
          </div>
          <div>
            <dt>Refunds</dt>
            <dd>{formatInr(day.refunded_paise)}</dd>
          </div>
          <div>
            <dt>Net received</dt>
            <dd>
              <strong>{formatInr(day.net_received_paise)}</strong>
            </dd>
          </div>
          {day.written_off_paise > 0 && (
            <div>
              <dt>Written off</dt>
              <dd>{formatInr(day.written_off_paise)}</dd>
            </div>
          )}
        </dl>
      </section>
      <section>
        <h3 className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            Every row{' '}
            <span className="hint">
              {rows.data ? `· ${paged.range}` : ''} · bills, payments and refunds as recorded
            </span>
          </span>
          {kinds.length > 1 && (
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              style={{ width: 'auto', textTransform: 'none', letterSpacing: 0 }}
              aria-label="Kind of row"
            >
              <option value="">Every kind</option>
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k] ?? k}
                </option>
              ))}
            </select>
          )}
        </h3>
        <ErrorNote error={rows.error} />
        {rows.isLoading ? (
          <Loading what="Reading the day's rows" />
        ) : shown.length === 0 ? (
          <div className="empty">No rows for this day.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="appr-table">
              <thead>
                <tr>
                  <th>What</th>
                  <th>Patient</th>
                  <th className="num">Amount</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {paged.rows.map((r) => (
                  <tr key={r.id} style={r.cancelled || r.deleted ? { opacity: 0.55 } : undefined}>
                    <td>
                      {KIND_LABEL[r.kind] ?? r.kind}
                      {r.number && <div className="faint mono">{r.number}</div>}
                    </td>
                    <td className="muted">
                      {r.patient_label ?? '—'}
                      {(r.method || r.counts_as) && (
                        <div className="faint">
                          {[r.method?.replace(/_/g, ' '), r.counts_as].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </td>
                    <td className="num">
                      {formatInr(r.amount_paise)}
                      {r.gst_paise > 0 && <div className="faint">GST {formatInr(r.gst_paise)}</div>}
                    </td>
                    <td>
                      {r.status && <Pill value={r.status} />}
                      {r.cancelled && <div className="faint">cancelled</div>}
                      {r.deleted && <div className="faint">no longer in the clinic’s records</div>}
                      {r.problem && <div className="faint">{r.problem}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pager page={paged.page} pages={paged.pages} onPage={paged.setPage} />
      </section>
    </Side>
  )
}

type Tab = 'days' | 'receipts' | 'settlements' | 'gst' | 'deliveries'

export default function ClinicBilling() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<Tab>('days')
  const [clinic, setClinic] = useState('')
  const [open, setOpen] = useState<BillingDay | null>(null)
  const [setup, setSetup] = useState(false)
  const [onlyCashOff, setOnlyCashOff] = useState(false)
  const [onlyAttention, setOnlyAttention] = useState(true)
  const [landed, setLanded] = useState('')
  const [search, setSearch] = useState('')
  const [onlySettleOff, setOnlySettleOff] = useState(false)
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['clinic-billing'] })

  const summary = useQuery({
    queryKey: ['clinic-billing', 'summary'],
    queryFn: () => api.get<BillingSummary>('/clinic-billing/summary'),
  })
  const days = useQuery({
    queryKey: ['clinic-billing', 'days', clinic],
    queryFn: () =>
      api.get<BillingDay[]>(`/clinic-billing/days${clinic ? `?clinic=${clinic}` : ''}`),
  })
  const gst = useQuery({
    queryKey: ['clinic-billing', 'gst', clinic],
    queryFn: () => api.get<GstRate[]>(`/clinic-billing/gst${clinic ? `?clinic=${clinic}` : ''}`),
  })
  const moneyIn = useQuery({
    queryKey: ['clinic-billing', 'money-in', clinic, onlyAttention],
    queryFn: () =>
      api.get<MoneyIn>(
        `/clinic-billing/money-in?only_attention=${onlyAttention}${clinic ? `&clinic=${clinic}` : ''}`,
      ),
  })
  const settled = useQuery({
    queryKey: ['clinic-billing', 'settlements'],
    queryFn: () => api.get<Settlements>('/clinic-billing/settlements'),
  })
  const pull = useMutation({
    mutationFn: () => api.post<Pull>('/clinic-billing/pull'),
    onSuccess: refresh,
  })

  // Lists, filtered on the client. Each is paged on its own.
  const dayList = (days.data ?? []).filter(
    (d) => !onlyCashOff || (d.cash_difference_paise !== null && d.cash_difference_paise !== 0),
  )
  const needle = search.trim().toLowerCase()
  const receiptList = (moneyIn.data?.receipts ?? []).filter(
    (r) =>
      (!landed || r.source_verdict === landed) &&
      (!needle ||
        [
          r.patient_label,
          r.receipt_no,
          r.invoice_number,
          r.method,
          (r.amount_paise / 100).toFixed(2),
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(needle))),
  )
  const settleList = (settled.data?.days ?? []).filter(
    (d) => (!clinic || d.clinic_code === clinic) && (!onlySettleOff || settlementOff(d)),
  )
  const deliveries = summary.data?.recent_deliveries ?? []
  const pagedDays = usePaged(dayList, `${clinic}|${onlyCashOff}`)
  const pagedReceipts = usePaged(receiptList, `${clinic}|${onlyAttention}|${landed}|${needle}`)
  const pagedSettle = usePaged(settleList, `${clinic}|${onlySettleOff}`)
  const pagedDeliveries = usePaged(deliveries, 'deliveries')

  if (summary.isLoading) return <Loading what="Loading the clinic billing feed" />
  const s = summary.data
  const since = s?.reconcile_from
    ? new Date(`${s.reconcile_from}T00:00:00`).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
      })
    : '1 Oct'
  const last = s?.last_delivery ?? null

  // The clinic picker keeps every clinic seen anywhere on the screen, so
  // choosing one does not shrink the list to only that one.
  const names = new Map<string, string>()
  for (const d of [...(days.data ?? []), ...(settled.data?.days ?? [])])
    names.set(d.clinic_code, d.clinic_name ?? names.get(d.clinic_code) ?? d.clinic_code)
  if (clinic && !names.has(clinic)) names.set(clinic, clinic)
  const clinics = Array.from(names.keys()).sort()

  const totals = (days.data ?? []).reduce(
    (t, d) => ({
      billed: t.billed + d.billed_paise,
      gst: t.gst + d.gst_paise,
      received: t.received + d.net_received_paise,
      cashOff: t.cashOff + (d.cash_difference_paise ? 1 : 0),
    }),
    { billed: 0, gst: 0, received: 0, cashOff: 0 },
  )
  const settleOff = (settled.data?.days ?? []).filter(
    (d) => (!clinic || d.clinic_code === clinic) && settlementOff(d),
  ).length
  const unplaced = settled.data?.unplaced ?? []
  const feedTrouble = !!s && (!s.secret_configured || (last?.refused ?? 0) > 0)

  const tabs: { key: Tab; label: string; count?: number; warn?: boolean }[] = [
    { key: 'days', label: 'Clinic-days', count: days.data?.length },
    { key: 'receipts', label: 'Receipts', count: moneyIn.data?.attention_count, warn: true },
    { key: 'settlements', label: 'Card & UPI vs bank', count: settleOff, warn: true },
    { key: 'gst', label: 'GST by rate' },
    { key: 'deliveries', label: 'Deliveries', count: deliveries.length },
  ]

  return (
    <>
      <ErrorNote
        error={
          summary.error ?? days.error ?? gst.error ?? pull.error ?? settled.error ?? moneyIn.error
        }
      />

      <Note>
        The clinic software’s own record of every bill and every rupee received, in every mode —
        sent to us as it changes. <strong>Nothing here is in the books yet.</strong> Revenue still
        comes from the takings sheet; this is held so the two can be compared, day by day.
      </Note>

      {s && !s.secret_configured && (
        <Note kind="warn">
          This server has no signing secret for the clinic’s push (CLINIC_WEBHOOK_SECRET), so every
          delivery is answered “not configured” and the clinic keeps retrying.
        </Note>
      )}
      {s && s.unknown_clinics.length > 0 && (
        <Note kind="warn">
          Rows name clinic codes that match no location here: {s.unknown_clinics.join(', ')}. They
          are kept and shown, and counted under their own code.
        </Note>
      )}

      <div className="bank-toolbar">
        <select
          value={clinic}
          onChange={(e) => setClinic(e.target.value)}
          style={{ width: 'auto' }}
          aria-label="Clinic"
        >
          <option value="">Every clinic</option>
          {clinics.map((c) => (
            <option key={c} value={c}>
              {names.get(c) && names.get(c) !== c ? `${names.get(c)} (${c})` : c}
            </option>
          ))}
        </select>
        <button className={feedTrouble ? undefined : 'quiet'} onClick={() => setSetup(true)}>
          Feed set-up{feedTrouble ? ' · needs a look' : ''}
        </button>
        <span className="bank-toolbar-link muted">
          {last ? `Last push ${when(last.received_at)}` : 'No push from the clinic yet'}
        </span>
      </div>

      <div className="grid">
        <Stat
          label={`Billed since ${since}`}
          value={formatInr(totals.billed)}
          note={`GST ${formatInr(totals.gst)} · voided bills excluded`}
        />
        <Stat
          label={`Received since ${since}`}
          value={formatInr(totals.received)}
          note="counter and advance payments, less refunds"
        />
        <Stat
          label="Receipts that need a look"
          value={moneyIn.data ? String(moneyIn.data.attention_count) : '…'}
          note="each against its invoice, and the drawer or the bank"
        />
        <Stat
          label="Days off"
          value={`${totals.cashOff} cash · ${settleOff} card/UPI`}
          note={
            unplaced.length
              ? `${unplaced.length} bank credit(s) match no clinic’s takings`
              : 'cash against the drawer, card and UPI against the bank'
          }
        />
      </div>

      <section className="card wide">
        <header className="appr-toolbar">
          <div className="seg" role="tablist">
            {tabs.map((t) => (
              <button
                key={t.key}
                role="tab"
                aria-selected={tab === t.key}
                className={tab === t.key ? 'on' : undefined}
                onClick={() => setTab(t.key)}
              >
                {t.label}
                {t.count !== undefined && (!t.warn || t.count > 0) && (
                  <span className="count">{t.count}</span>
                )}
              </button>
            ))}
          </div>
          <div className="appr-filters">
            {tab === 'days' && (
              <>
                <label className="appr-check">
                  <input
                    type="checkbox"
                    checked={onlyCashOff}
                    onChange={(e) => setOnlyCashOff(e.target.checked)}
                  />
                  only where cash differs
                </label>
                <span className="muted" style={{ fontSize: 13 }}>
                  {pagedDays.range} day(s)
                </span>
              </>
            )}
            {tab === 'receipts' && (
              <>
                <input
                  type="search"
                  placeholder="Patient, receipt, invoice or amount"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search receipts"
                />
                <select
                  value={landed}
                  onChange={(e) => setLanded(e.target.value)}
                  aria-label="Where it landed"
                >
                  <option value="">Landed anywhere</option>
                  {(moneyIn.data?.totals ?? []).map((x) => (
                    <option key={x.verdict} value={x.verdict}>
                      {x.verdict} ({x.count})
                    </option>
                  ))}
                </select>
                <label className="appr-check">
                  <input
                    type="checkbox"
                    checked={onlyAttention}
                    onChange={(e) => setOnlyAttention(e.target.checked)}
                  />
                  only the ones that need a look
                </label>
              </>
            )}
            {tab === 'settlements' && (
              <label className="appr-check">
                <input
                  type="checkbox"
                  checked={onlySettleOff}
                  onChange={(e) => setOnlySettleOff(e.target.checked)}
                />
                only days that don’t agree
              </label>
            )}
          </div>
        </header>

        <div className="body tight">
          {tab === 'days' &&
            (days.isLoading ? (
              <Loading what="Adding up the days" />
            ) : dayList.length === 0 ? (
              <div className="empty">
                {onlyCashOff
                  ? 'Every day’s cash agrees with the drawer.'
                  : 'Nothing from the clinic’s billing yet.'}
              </div>
            ) : (
              <table className="appr-table">
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Clinic</th>
                    <th className="num">Bills</th>
                    <th className="num">Billed</th>
                    <th className="num">GST</th>
                    <th className="num">Cash</th>
                    <th className="num">UPI</th>
                    <th className="num">Card</th>
                    <th className="num">Other</th>
                    <th className="num">Refunds</th>
                    <th className="num">Received</th>
                    <th>Cash vs drawer</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedDays.rows.map((d) => {
                    const on =
                      open?.business_date === d.business_date && open?.clinic_code === d.clinic_code
                    return (
                      <tr
                        key={`${d.clinic_code}-${d.business_date}`}
                        className={`appr-row${on ? ' selected' : ''}`}
                        tabIndex={0}
                        title="Open the day’s rows"
                        onClick={() => setOpen(d)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setOpen(d)
                          }
                        }}
                      >
                        <td className="date">{d.business_date}</td>
                        <td>{d.clinic_name ?? d.clinic_code}</td>
                        <td className="num">
                          {d.invoices}
                          {d.voided > 0 && <div className="faint">{d.voided} voided</div>}
                        </td>
                        <td className="num">{formatInr(d.billed_paise)}</td>
                        <td className="num">{formatInr(d.gst_paise)}</td>
                        <td className="num">{formatInr(d.received_cash_paise)}</td>
                        <td className="num">{formatInr(d.received_upi_paise)}</td>
                        <td className="num">{formatInr(d.received_card_paise)}</td>
                        <td className="num">{formatInr(d.received_other_paise)}</td>
                        <td className="num">{formatInr(d.refunded_paise)}</td>
                        <td className="num">
                          <strong>{formatInr(d.net_received_paise)}</strong>
                        </td>
                        <td>
                          <CashCheck d={d} />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            ))}

          {tab === 'receipts' &&
            (moneyIn.isLoading || !moneyIn.data ? (
              <Loading what="Matching every receipt" />
            ) : (
              <>
                <div className="faint" style={{ padding: '10px 16px 0' }}>
                  {moneyIn.data.receipts_count} receipts, {formatInr(moneyIn.data.receipts_paise)},
                  from {moneyIn.data.start}
                  {clinic ? ` at ${names.get(clinic) ?? clinic}` : ''}. Cash is matched to the
                  clinic’s drawer; UPI, card, cheque and transfers to the bank statement
                  {moneyIn.data.statement_through
                    ? `, which runs to ${moneyIn.data.statement_through}`
                    : ''}
                  .
                </div>
                <div className="row" style={{ gap: 8, padding: '10px 16px', flexWrap: 'wrap' }}>
                  {moneyIn.data.totals.map((x) => (
                    <button
                      key={x.verdict}
                      type="button"
                      className={`pill ${SOURCE_CLASS[x.verdict] ?? 'advisory'}`}
                      style={{
                        cursor: 'pointer',
                        border: 0,
                        minHeight: 0,
                        outline: landed === x.verdict ? '2px solid var(--accent)' : undefined,
                      }}
                      title={landed === x.verdict ? 'Show every receipt' : 'Show only these'}
                      onClick={() => setLanded(landed === x.verdict ? '' : x.verdict)}
                    >
                      {x.verdict}: {x.count} · {formatInr(x.amount_paise)}
                    </button>
                  ))}
                  <span className="muted" style={{ fontSize: 13, marginLeft: 'auto' }}>
                    showing {pagedReceipts.range}
                  </span>
                </div>
                {receiptList.length === 0 ? (
                  <div className="empty">
                    {needle || landed
                      ? 'No receipt matches that.'
                      : onlyAttention
                        ? 'Every receipt is accounted for.'
                        : 'No receipts yet.'}
                  </div>
                ) : (
                  <table className="appr-table">
                    <thead>
                      <tr>
                        <th>Day</th>
                        <th>Clinic</th>
                        <th>Patient</th>
                        <th>How</th>
                        <th className="num">Amount</th>
                        <th>Invoice</th>
                        <th>Where it landed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedReceipts.rows.map((r) => (
                        <tr key={r.feed_id}>
                          <td className="date">{r.business_date}</td>
                          <td>{r.clinic_code}</td>
                          <td className="muted">
                            {r.patient_label ?? '—'}
                            {r.receipt_no && <div className="faint mono">{r.receipt_no}</div>}
                          </td>
                          <td className="muted">
                            {r.method.replace(/_/g, ' ')}
                            {r.counts_as === 'advance' && <div className="faint">advance</div>}
                          </td>
                          <td className="num">
                            <strong>{formatInr(r.amount_paise)}</strong>
                          </td>
                          <td>
                            <span className={`pill ${INVOICE_CLASS[r.invoice_verdict] ?? 'draft'}`}>
                              {r.invoice_verdict === 'matched'
                                ? (r.invoice_number ?? 'matched')
                                : r.invoice_verdict}
                            </span>
                            {r.invoice_verdict !== 'matched' && r.invoice_note && (
                              <div className="faint">{r.invoice_note}</div>
                            )}
                            {r.invoice_verdict === 'matched' && r.invoice_status && (
                              <div className="faint">
                                {r.invoice_status.replace(/_/g, ' ')}
                                {r.invoice_total_paise !== null &&
                                  ` · bill ${formatInr(r.invoice_total_paise)}`}
                              </div>
                            )}
                          </td>
                          <td>
                            <span
                              className={`pill ${SOURCE_CLASS[r.source_verdict] ?? 'advisory'}`}
                            >
                              {r.source_verdict}
                            </span>
                            {r.source_note && <div className="faint">{r.source_note}</div>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            ))}

          {tab === 'settlements' &&
            (settled.isLoading ? (
              <Loading what="Reading the bank statement against the takings" />
            ) : (
              <>
                <div className="faint" style={{ padding: '10px 16px' }}>
                  Rates: {settled.data?.rates}. The card network is not on the clinic’s rows, so a
                  day’s cards pass when the bank kept between 0% (RuPay) and 1.888% (a premium or
                  corporate card, which the machine cannot tell from an ordinary one).
                  {settled.data?.statement_through &&
                    ` The bank statement runs to ${settled.data.statement_through}.`}
                  {settleList.length > PAGE && ` Showing ${pagedSettle.range} day(s).`}
                </div>
                {settleList.length === 0 ? (
                  <div className="empty">
                    {onlySettleOff
                      ? 'Every day’s card and UPI agree with the bank.'
                      : 'No card or UPI takings yet.'}
                  </div>
                ) : (
                  <table className="appr-table">
                    <thead>
                      <tr>
                        <th>Day</th>
                        <th>Clinic</th>
                        <th className="num">Card taken</th>
                        <th className="num">Bank paid</th>
                        <th className="num">Kept (MDR)</th>
                        <th>Cards</th>
                        <th className="num">UPI taken</th>
                        <th className="num">UPI on statement</th>
                        <th>UPI</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedSettle.rows.map((d) => (
                        <tr key={`${d.clinic_code}-${d.business_date}`}>
                          <td className="date">{d.business_date}</td>
                          <td>{d.clinic_name ?? d.clinic_code}</td>
                          <td className="num">
                            {d.card_gross_paise ? formatInr(d.card_gross_paise) : '—'}
                            {d.card_credit_paise > 0 && (
                              <div className="faint">{formatInr(d.card_credit_paise)} credit</div>
                            )}
                          </td>
                          <td className="num">
                            {d.card_settled_paise === null ? '—' : formatInr(d.card_settled_paise)}
                          </td>
                          <td className="num">
                            {d.mdr_paise === null ? '—' : formatInr(d.mdr_paise)}
                          </td>
                          <td>
                            <Verdict value={d.card_verdict} />
                            {d.card_note && <div className="faint">{d.card_note}</div>}
                          </td>
                          <td className="num">
                            {d.upi_taken_paise ? formatInr(d.upi_taken_paise) : '—'}
                          </td>
                          <td className="num">
                            {d.upi_on_statement_paise ? formatInr(d.upi_on_statement_paise) : '—'}
                          </td>
                          <td>
                            <Verdict value={d.upi_verdict} />
                            {d.upi_note && <div className="faint">{d.upi_note}</div>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            ))}

          {tab === 'gst' &&
            ((gst.data ?? []).length === 0 ? (
              <div className="empty">No taxed lines yet.</div>
            ) : (
              <table className="appr-table">
                <thead>
                  <tr>
                    <th>Rate</th>
                    <th className="num">Taxable value</th>
                    <th className="num">GST</th>
                  </tr>
                </thead>
                <tbody>
                  {(gst.data ?? []).map((g) => (
                    <tr key={g.rate}>
                      <td>{g.rate}%</td>
                      <td className="num">{formatInr(g.taxable_paise)}</td>
                      <td className="num">{formatInr(g.tax_paise)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td>
                      <strong>Total since {since}</strong>
                    </td>
                    <td className="num">
                      <strong>
                        {formatInr((gst.data ?? []).reduce((t, g) => t + g.taxable_paise, 0))}
                      </strong>
                    </td>
                    <td className="num">
                      <strong>
                        {formatInr((gst.data ?? []).reduce((t, g) => t + g.tax_paise, 0))}
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            ))}

          {tab === 'deliveries' &&
            (deliveries.length === 0 ? (
              <div className="empty">No deliveries yet.</div>
            ) : (
              <table className="appr-table">
                <thead>
                  <tr>
                    <th>Received</th>
                    <th className="num">Changes</th>
                    <th className="num">Applied</th>
                    <th className="num">Older than held</th>
                    <th className="num">Refused</th>
                    <th>Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedDeliveries.rows.map((d) => (
                    <tr key={d.delivery_id}>
                      <td className="date">{when(d.received_at)}</td>
                      <td className="num">{d.event_count}</td>
                      <td className="num">{d.applied}</td>
                      <td className="num">{d.stale}</td>
                      <td className="num">{d.refused}</td>
                      <td>
                        <Pill value={d.outcome} />
                        {d.problem && <div className="faint">{d.problem}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}
        </div>

        {tab === 'days' && (
          <Pager page={pagedDays.page} pages={pagedDays.pages} onPage={pagedDays.setPage} />
        )}
        {tab === 'receipts' && (
          <Pager
            page={pagedReceipts.page}
            pages={pagedReceipts.pages}
            onPage={pagedReceipts.setPage}
          />
        )}
        {tab === 'settlements' && (
          <Pager page={pagedSettle.page} pages={pagedSettle.pages} onPage={pagedSettle.setPage} />
        )}
        {tab === 'deliveries' && (
          <Pager
            page={pagedDeliveries.page}
            pages={pagedDeliveries.pages}
            onPage={pagedDeliveries.setPage}
          />
        )}
      </section>

      {tab === 'settlements' &&
        (unplaced.length > 0 || (settled.data?.terminals ?? []).length > 0) && (
          <section className="card wide">
            <header>
              <div>
                <strong>Bank credits that match no clinic’s takings</strong>{' '}
                <span className="hint">· {unplaced.length} line(s)</span>
              </div>
            </header>
            <div className="body tight">
              {unplaced.length === 0 ? (
                <div className="empty">Every card and UPI credit found its clinic-day.</div>
              ) : (
                <table className="appr-table">
                  <thead>
                    <tr>
                      <th>Bank date</th>
                      <th className="num">Amount</th>
                      <th>Terminal</th>
                      <th>Why</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unplaced.map((u, i) => (
                      <tr key={`${u.txn_date}-${u.amount_paise}-${i}`}>
                        <td className="date">{u.txn_date}</td>
                        <td className="num">{formatInr(u.amount_paise)}</td>
                        <td className="muted mono">{u.terminal ?? '—'}</td>
                        <td className="faint">
                          {u.for_date && `for ${u.for_date} · `}
                          {u.note}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {(settled.data?.terminals ?? []).length > 0 && (
                <div className="faint" style={{ padding: '10px 16px' }}>
                  Terminals learnt from matching days:{' '}
                  {(settled.data?.terminals ?? [])
                    .map((x) => `${x.terminal} → ${x.clinic_code}`)
                    .join(' · ')}
                </div>
              )}
            </div>
          </section>
        )}

      {open && <DayPanel day={open} onClose={() => setOpen(null)} />}

      {setup && s && (
        <Side
          kicker="Clinic billing"
          title="Feed set-up"
          sub="where the clinic sends its changes, and the nightly backstop"
          onClose={() => setSetup(false)}
        >
          <section>
            <h3>The clinic’s push</h3>
            <div className="muted" style={{ marginBottom: 6 }}>
              Give the clinic’s administrator this address, under Admin › API keys › Finance
              webhook:
            </div>
            <div className="mono" style={{ wordBreak: 'break-all', marginBottom: 10 }}>
              {s.webhook_url}
            </div>
            <dl className="appr-facts">
              <div>
                <dt>Signing secret</dt>
                <dd>{s.secret_configured ? 'set' : 'not set on this server'}</dd>
              </div>
              <div>
                <dt>Last delivery</dt>
                <dd>
                  {last
                    ? `${when(last.received_at)} · ${last.event_count} change(s), ${last.applied} applied${last.refused ? `, ${last.refused} refused` : ''}`
                    : 'none yet — the clinic sends changes every five minutes once it has our address'}
                </dd>
              </div>
              <div>
                <dt>Refused changes</dt>
                <dd>{s.refused_events}</dd>
              </div>
            </dl>
          </section>
          <section>
            <h3>Backstop pull</h3>
            <dl className="appr-facts">
              <div>
                <dt>Last read</dt>
                <dd>
                  {s.last_pull
                    ? `${s.last_pull.outcome.replace(/_/g, ' ')} · ${when(s.last_pull.started_at)} · ${s.last_pull.rows_seen} rows`
                    : 'never — reads the last 35 days once a night'}
                </dd>
              </div>
            </dl>
            {s.last_pull?.outcome === 'no_scope' && (
              <Note kind="warn">
                The key reads the cash drawer only; ask the clinic for finance:read.
              </Note>
            )}
            {s.last_pull?.error && <div className="faint">last read: {s.last_pull.error}</div>}
            {can('clinic.billing.post') && (
              <div style={{ marginTop: 10 }}>
                <button disabled={pull.isPending} onClick={() => pull.mutate()}>
                  {pull.isPending ? 'Reading…' : 'Read the last 35 days now'}
                </button>
                <ErrorNote error={pull.error} />
              </div>
            )}
          </section>
          <section>
            <h3>Rows held, by kind</h3>
            <dl className="appr-facts">
              {Object.entries(s.records_by_kind).map(([k, n]) => (
                <div key={k}>
                  <dt>{KIND_LABEL[k] ?? k}</dt>
                  <dd>{n}</dd>
                </div>
              ))}
            </dl>
          </section>
        </Side>
      )}
    </>
  )
}
